/* eslint-disable */

/**
 * Plain-language explanations for the production numbers on Today's Huddle.
 *
 * This is the ONE place the copy lives — the inline info icons (MetricInfo.vue) and the
 * "What do these numbers mean?" modal (MetricsExplainerModal.vue) both render from here, so
 * they can never drift from each other.
 *
 * Every explanation below was verified against the code that produces the number:
 *   - frontend  src/pages/Huddle/Huddle.vue  (netProductionToday, getScheduledProductionToday,
 *              getClinicalHoursToday, getMonthlyProductionGoal, monthlyScheduledGoal)
 *   - backend   teamio-serverless-api/huddle-service/sql/huddle.js  (grossProduction, writeOff,
 *              adjustments, collections, scheduledThisMonth, monthlyProductionGoal)
 * If you change how a number is calculated, change the copy here in the same commit.
 *
 * Entry shape:
 *   key        unique slug; also the modal section id and the Help Center anchor
 *   label      the on-screen wording this explains
 *   short      one or two sentences — what the number is
 *   formula    how it's built, in words a practice would use
 *   gotcha     the "why doesn't this match what I expected" part (optional, but it's the
 *              reason this feature exists — most entries should have one)
 */

const DOCS_BASE = 'https://docs.teamio.app/tasks/how-huddle-numbers-are-calculated/';

const entries = [
  {
    key: 'completed-production-net',
    label: 'Completed Production (Net)',
    short: 'The production you have actually completed so far — procedures marked Complete in Open Dental, after insurance write-offs and adjustments.',
    formula: 'Gross production − insurance write-offs + adjustments',
    gotcha: 'This starts at $0 each morning and climbs as patients are seen. It only becomes accurate once the claim is generated at checkout — generated, not sent. The write-offs that make production "net" do not exist until the claim does, so if your team batches claims at the end of the day, today\'s number will look low until they do.'
  },
  {
    key: 'production-per-hour',
    label: '$/HR',
    short: 'Completed production divided by the provider\'s clinical hours for the day.',
    formula: 'Completed Production (Net) ÷ clinical hours',
    gotcha: 'The provider\'s day runs from the earlier of the start of their Open Dental schedule and their first appointment, to the later of the end of their schedule and the end of their last appointment. If the schedule has overlapping entries, that time is counted once. Lunch is the amount you set under Set Goals, or the empty gap in a split schedule (for example 8-12 and 1-5) if that is larger — never both. A provider with no lunch set has no lunch subtracted. Hygienists are counted from the appointment\'s Hygienist field. That is paid chair time, not hours worked — gaps and broken appointments still count. Shows N/A when there are no appointments to measure.'
  },
  {
    key: 'on-schedule-net',
    label: 'On Schedule (Net)',
    short: 'The full day\'s value: what has already been completed, plus the estimated value of the appointments still to come.',
    formula: 'Completed Production (Net) + value of the appointments still scheduled',
    gotcha: 'The two halves are measured differently. The completed part is net of real write-offs and adjustments. The part still on the schedule is estimated from the patient\'s insurance fee schedule minus any discounts — there is no claim yet, so no write-off or adjustment has happened. Read this first thing in the morning to see what the day is worth before anyone sits down.'
  },
  {
    key: 'scheduled-per-hour',
    label: 'Sched $/HR',
    short: 'On Schedule (Net) divided by the same clinical hours used for $/HR.',
    formula: 'On Schedule (Net) ÷ clinical hours',
    gotcha: 'Read in the morning, this is what the day is set up to produce per chair hour. Compare it to $/HR as the day goes on to see whether you are tracking to it.'
  },
  {
    key: 'net-production-mtd',
    label: 'Month-to-date',
    short: 'Everything this provider has completed from the 1st of the month through the date you are viewing.',
    formula: 'Completed Production (Net), added up from the 1st through the selected date',
    gotcha: 'This follows the date picker at the top of the page. Pick a day last week and it reads month-to-date as of that day, not as of today.'
  },
  {
    key: 'scheduled-this-month',
    label: 'This month (on schedule)',
    short: 'What the rest of the month is set up to be: everything completed so far, plus everything still booked between the date you are viewing and the end of the month.',
    formula: 'Net production so far + value of the appointments still scheduled through month end',
    gotcha: 'This one looks forward while the Month-to-date figure beside it looks back. They deliberately cover different stretches of the same month — one is what you have done, the other is what the month is set up to be.'
  },
  {
    key: 'provider-goal-bar',
    label: '% of goal (per provider)',
    short: 'How this provider\'s month compares to everything currently booked on their schedule for the month.',
    formula: 'The amount ÷ the value of every appointment on this provider\'s schedule for the calendar month',
    gotcha: 'This bar measures against the schedule, not against a target anyone set. It is what is on the books for the whole month, so it moves every time an appointment is added or cancelled. The bar on the right side of the page, under Total On Schedule, is the one measured against your Set Goals target — which is why the two show different dollar figures.'
  },
  {
    key: 'office-goal-bar',
    label: '% of goal (office)',
    short: 'The whole office\'s month measured against the production goal you set.',
    formula: 'Scheduled month-to-date ÷ your monthly goal from Set Goals',
    gotcha: 'The goal comes from the per-weekday production goals under the Set Goals gear, added up across every day of the month. Every provider who has a goal counts toward it, whether or not they are on today\'s schedule. This is the real target — the % of goal bars in the provider table measure against booked schedule instead.'
  },
  {
    key: 'collection',
    label: 'Collection',
    short: 'Money actually received — patient payments plus insurance checks.',
    formula: 'Patient payments + insurance payments received',
    gotcha: 'Counted by the date the payment or check was posted, which is usually not the day the treatment was done. Collection and production will not match on any given day, and they are not meant to.'
  }
];

// Every entry gets its Help Center deep link from its key, so the anchors can't fall out of sync.
entries.forEach(entry => {
  entry.docsAnchor = `${DOCS_BASE}#${entry.key}`;
});

const byKey = entries.reduce((acc, entry) => {
  acc[entry.key] = entry;
  return acc;
}, {});

export { entries, byKey, DOCS_BASE };
export default entries;
